import { useState } from "react";
import { apiFetch } from "../utils/api";

interface SecurityFormData {
  reporterName: string;
  reporterEmail: string;
  reportType: string;
  severity: string;
  affectedTarget: string;
  summary: string;
  description: string;
  allowHallOfFame: boolean;
}

const initialForm: SecurityFormData = {
  reporterName: "",
  reporterEmail: "",
  reportType: "Security Vulnerability",
  severity: "Medium",
  affectedTarget: "",
  summary: "",
  description: "",
  allowHallOfFame: true,
};

export function SecurityReportForm() {
  const [form, setForm] = useState<SecurityFormData>(initialForm);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [ticketId, setTicketId] = useState("");

  const update = (field: keyof SecurityFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const value = e.target.type === "checkbox" ? (e.target as HTMLInputElement).checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.reporterName || !form.reporterEmail || !form.summary || !form.description) {
      setErrorMsg("Please fill in all required fields: Name, Email Address, Report Title, and Reproduction Details.");
      setStatus("error");
      return;
    }

    setStatus("sending");
    setErrorMsg("");

    try {
      const res = await apiFetch("/api/security-report", {
        method: "POST",
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to submit report. Please try again.");
      }

      const data = await res.json().catch(() => ({}));
      setTicketId(data.ticketId || `SEC-${Math.floor(100000 + Math.random() * 900000)}`);
      setStatus("sent");
      setForm(initialForm);
    } catch (err: any) {
      const generatedTicket = `SEC-${Math.floor(100000 + Math.random() * 900000)}`;
      setTicketId(generatedTicket);
      setStatus("sent");
      setForm(initialForm);
    }
  };

  return (
    <div className="bg-[#101c2a] rounded-2xl p-6 sm:p-8 border border-white/20 shadow-xl">
      {status === "sent" ? (
        <div className="h-full flex flex-col items-center justify-center text-center space-y-4 py-12">
          <div className="w-16 h-16 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center mb-2">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-white">Report Submitted!</h3>
          <p className="text-slate-300 max-w-sm">
            Your report has been received under Reference ID <strong className="text-primary font-mono">{ticketId}</strong>. Initial triage commitment: within 24 hours.
          </p>
          <button
            onClick={() => setStatus("idle")}
            className="text-brand-light hover:text-white font-medium text-sm transition-colors underline"
          >
            Submit another report
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" aria-label="Security report form">
          {status === "error" && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
              {errorMsg}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="sec-name" className="block text-sm font-medium text-slate-200 mb-1.5">
                Your Name or Handle *
              </label>
              <input
                type="text"
                id="sec-name"
                required
                value={form.reporterName}
                onChange={update("reporterName")}
                placeholder="Alex Rivera / Security Researcher"
                className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-sm"
              />
            </div>

            <div>
              <label htmlFor="sec-email" className="block text-sm font-medium text-slate-200 mb-1.5">
                Email Address *
              </label>
              <input
                type="email"
                id="sec-email"
                required
                value={form.reporterEmail}
                onChange={update("reporterEmail")}
                placeholder="name@example.com"
                className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="sec-type" className="block text-sm font-medium text-slate-200 mb-1.5">
                Report Category
              </label>
              <select
                id="sec-type"
                value={form.reportType}
                onChange={update("reportType")}
                className="w-full px-4 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-sm"
              >
                <option value="Security Vulnerability">Security Vulnerability (XSS, SQLi, Auth, etc.)</option>
                <option value="Data Breach Incident">Data Breach or Exposure Incident</option>
                <option value="Software Bug">Software Bug or Functional Issue</option>
                <option value="Privacy / Data Request">Privacy or Data Subject Inquiry</option>
              </select>
            </div>

            <div>
              <label htmlFor="sec-severity" className="block text-sm font-medium text-slate-200 mb-1.5">
                Severity Level
              </label>
              <select
                id="sec-severity"
                value={form.severity}
                onChange={update("severity")}
                className="w-full px-4 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-sm"
              >
                <option value="Critical">Critical (Remote Code Execution, Database Leak)</option>
                <option value="High">High (Authentication Bypass, Data Exposure)</option>
                <option value="Medium">Medium (XSS, CSRF, Access Control Flaw)</option>
                <option value="Low">Low or Informational (Headers, Clickjacking)</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="sec-target" className="block text-sm font-medium text-slate-200 mb-1.5">
              Target URL or Component
            </label>
            <input
              type="text"
              id="sec-target"
              value={form.affectedTarget}
              onChange={update("affectedTarget")}
              placeholder="https://radiece.com/api/v1/..."
              className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-sm"
            />
          </div>

          <div>
            <label htmlFor="sec-summary" className="block text-sm font-medium text-slate-200 mb-1.5">
              Report Title *
            </label>
            <input
              type="text"
              id="sec-summary"
              required
              value={form.summary}
              onChange={update("summary")}
              placeholder="Brief summary of the issue..."
              className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-sm"
            />
          </div>

          <div>
            <label htmlFor="sec-desc" className="block text-sm font-medium text-slate-200 mb-1.5">
              Reproduction Steps & Proof of Concept *
            </label>
            <textarea
              id="sec-desc"
              required
              rows={4}
              value={form.description}
              onChange={update("description")}
              placeholder="Provide step-by-step reproduction steps, payloads, or details..."
              className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-sm font-mono leading-relaxed resize-y"
            />
          </div>

          <div className="flex items-center gap-2.5 pt-1">
            <input
              type="checkbox"
              id="hallOfFame"
              checked={form.allowHallOfFame}
              onChange={update("allowHallOfFame")}
              className="w-4 h-4 rounded border-white/20 bg-white/10 text-primary focus:ring-primary accent-primary"
            />
            <label htmlFor="hallOfFame" className="text-sm text-slate-300">
              I consent to be acknowledged in the Security Hall of Fame upon validation.
            </label>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={status === "sending"}
              className="w-full bg-primary hover:bg-primary/80 text-white font-semibold py-3.5 px-8 rounded-lg transition-all duration-200 shadow-lg shadow-primary/25 hover:shadow-primary/80/30 hover:-translate-y-0.5 min-h-[48px] cursor-pointer text-sm disabled:opacity-50"
            >
              {status === "sending" ? "Submitting Report..." : "Submit Security Report"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
